import React from "react";

/* ─── Inline formatting ─────────────────────────────────────────────── */

const INLINE_RE = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g;

function inlineFormat(text) {
  const segs = [];
  let last = 0, m;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) segs.push({ t: "text", c: text.slice(last, m.index) });
    const raw = m[0];
    if (raw.startsWith("**"))     segs.push({ t: "bold",   c: raw.slice(2, -2) });
    else if (raw.startsWith("*")) segs.push({ t: "italic", c: raw.slice(1, -1) });
    else                          segs.push({ t: "code",   c: raw.slice(1, -1) });
    last = m.index + raw.length;
  }
  if (last < text.length) segs.push({ t: "text", c: text.slice(last) });
  if (segs.length === 0) return text;

  return segs.map((s, i) => {
    if (s.t === "bold")   return <strong key={i} className="font-semibold">{s.c}</strong>;
    if (s.t === "italic") return <em     key={i} className="italic text-slate-500 dark:text-slate-400">{s.c}</em>;
    if (s.t === "code")   return (
      <code key={i} className="bg-slate-100/80 dark:bg-slate-900/70 text-[#1b4f9c] dark:text-sky-400 px-1.5 py-0.5 rounded-[4px] text-[0.85em] font-mono tracking-tight">
        {s.c}
      </code>
    );
    return s.c;
  });
}

/* ─── Text block — paragraphs, lists, headers, blockquotes ─────────── */

function KeviaTextBlock({ text }) {
  const paragraphs = text.split(/\n{2,}/);

  return (
    <>
      {paragraphs.map((para, pi) => {
        if (!para.trim()) return null;

        // --- H3 header ---
        if (/^###\s/.test(para)) {
          return (
            <p key={pi} className="font-semibold text-[0.95em] text-slate-800 dark:text-slate-100 tracking-tight mt-0.5">
              {para.replace(/^###\s+/, "")}
            </p>
          );
        }

        // --- Blockquote ---
        if (/^>\s/.test(para)) {
          return (
            <div key={pi} className="border-l-[2px] border-slate-300/50 dark:border-slate-600/50 pl-3 text-slate-500 dark:text-slate-400 italic">
              {inlineFormat(para.slice(2))}
            </div>
          );
        }

        const lines = para.split("\n").filter(Boolean);

        // --- Unordered list ---
        if (lines.length > 0 && lines.every((l) => /^[-*•]\s/.test(l.trim()))) {
          return (
            <ul key={pi} className="space-y-1.5 pl-0.5">
              {lines.map((l, li) => (
                <li key={li} className="flex gap-2.5 items-start">
                  <span
                    className="text-[#1b4f9c]/50 dark:text-sky-400/50 mt-[5px] shrink-0 select-none"
                    style={{ fontSize: "6px" }}
                  >
                    ●
                  </span>
                  <span>{inlineFormat(l.replace(/^[-*•]\s+/, ""))}</span>
                </li>
              ))}
            </ul>
          );
        }

        // --- Ordered list ---
        if (lines.length > 0 && lines.every((l) => /^\d+\.\s/.test(l.trim()))) {
          return (
            <ol key={pi} className="space-y-1.5 pl-0.5">
              {lines.map((l, li) => (
                <li key={li} className="flex gap-2.5 items-start">
                  <span className="text-slate-400/70 dark:text-slate-500/70 shrink-0 text-[0.85em] mt-[1px] tabular-nums">
                    {li + 1}.
                  </span>
                  <span>{inlineFormat(l.replace(/^\d+\.\s+/, ""))}</span>
                </li>
              ))}
            </ol>
          );
        }

        // --- Plain paragraph ---
        return (
          <p key={pi}>
            {lines.map((line, li) => (
              <React.Fragment key={li}>
                {inlineFormat(line)}
                {li < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}

/* ─── Main component ─────────────────────────────────────────────────── */

/**
 * KeviaMarkdown — renders Kevia's response with pastoral typography.
 *
 * @param {string} content
 * @param {"md"|"sm"} size — "md" (14px, KeviaChat) | "sm" (13px, KeviaPanel)
 */
export function KeviaMarkdown({ content, size = "md" }) {
  if (!content) return null;

  const textSize  = size === "sm" ? "text-[13px]" : "text-[14px]";
  const textColor = "text-slate-700 dark:text-slate-200/90";

  // Split on fenced code blocks; odd-indexed segments are code
  const segments = content.split(/(```[\s\S]*?```)/g);

  return (
    <div className={`space-y-2.5 ${textSize} ${textColor} leading-[1.7]`}>
      {segments.map((seg, i) => {
        if (i % 2 === 1) {
          const code = seg.replace(/^```[^\n]*\n?/, "").replace(/\n?```$/, "");
          return (
            <pre
              key={i}
              className="bg-slate-900/90 dark:bg-slate-950/80 text-slate-100 rounded-[12px] px-3.5 py-3 text-[12px] font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed border-l-2 border-[#1b4f9c]/30"
            >
              {code}
            </pre>
          );
        }
        if (!seg.trim()) return null;
        return <KeviaTextBlock key={i} text={seg} />;
      })}
    </div>
  );
}

export default KeviaMarkdown;
